import { applyNodeChanges, type Node, type NodeChange } from '@xyflow/react'
import { getParameterValue } from '@/features/backtest/components/nodes/universal-node-helpers'

export function parseTimeframeToMinutes(timeframe: string): number {
  const value = parseInt(timeframe)
  if (isNaN(value)) return 1440
  if (timeframe.endsWith('m')) return value
  if (timeframe.endsWith('h')) return value * 60
  if (timeframe.endsWith('D')) return value * 1440
  if (timeframe.endsWith('W')) return value * 10080
  if (timeframe.endsWith('M')) return value * 43200
  return 1440
}

export function getLowestTimeframePriceNode<T extends Node>(nodes: T[]): T | null {
  const priceNodes = nodes.filter((node) => node.type === 'priceData' || node.type === 'marketData')
  if (priceNodes.length === 0) return null

  const timeframe = (node: T): string =>
    (getParameterValue(node.data?.parameters, 'timeframe', '1D') as string) || '1D'

  return priceNodes.reduce((lowest, current) =>
    parseTimeframeToMinutes(timeframe(current)) < parseTimeframeToMinutes(timeframe(lowest))
      ? current
      : lowest,
  )
}

export function ensurePrimaryPriceData<T extends Node>(nodes: T[]): T[] {
  if (nodes.some((node) => node.type === 'priceData' && node.data?.isPrimary === true)) {
    return nodes
  }

  const primary = getLowestTimeframePriceNode(nodes.filter((node) => node.type === 'priceData'))
  if (!primary) return nodes

  return nodes.map((node) => {
    if (node.id !== primary.id) return node
    const data: Record<string, unknown> = { ...node.data, isPrimary: true }
    delete data.lockedParams
    return { ...node, data }
  }) as T[]
}

export function applyNodeChangesWithPrimaryOwnership<T extends Node>(
  changes: NodeChange<T>[],
  nodes: T[],
): T[] {
  const next = applyNodeChanges(changes, nodes)
  return changes.some((change) => change.type === 'remove') ? ensurePrimaryPriceData(next) : next
}
